import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { SkusServerProps } from '@/types/props/pages';
import useBackendUser from '@/hooks/useBackendUser';
import useWindowWidth from '@/hooks/useWindowWidth';
import { useRouter } from 'next/router';
import { WishlistContext } from '@/context/wishlistContext';
import SkusSearchLayout from './SkusSearchLayout';
import ResponsiveComponent from '../common/ResponsiveComponent';
import SkusDesktop from './SkusDesktop';
import SkusMobile from './SkusMobile/SkusMobile';
import SkusBrandContent from './SkusBrandContent';
import { useSearchParams } from 'next/navigation';
import ConfigureFix from '@/components/skus/algoliaCustom/ConfigureFix';
import { AllSortsCustomTranslated } from '@/types/other';
import envPublic from '@/config/env.public';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';

const SkusEntirePage = ({
  windowWidthSv,
  metaTitle,
  h1Title,
  user: userSSR,
  brandsContent,
  hasFilterCookie,
  customBaseUrl,
  countrySlug,
  includeCategoryNav = true,
}: SkusServerProps) => {
  const searchParams = useSearchParams();
  const { t } = useTranslation('index');
  const { t: tCommon } = useTranslation('common');
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const [isHasAskToggleSelected, setIsHasAskToggleSelected] = useState(
    searchParams?.get('hasAsk') === 'true'
  );
  const { data: user = null } = useBackendUser();
  const windowWidth = useWindowWidth(windowWidthSv);
  const router = useRouter();
  const wishlistContext = useContext(WishlistContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allSortsCustomTranslated, _setAllSortsCustomTranslated] = useState<
    AllSortsCustomTranslated[]
  >(() => {
    const allSortsCustomTranslated = [
      {
        label: translate(CommonEnum.most_popular),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_MOST_POPULAR,
        name: translate(CommonEnum.most_popular),
      },
      {
        label: translate(CommonEnum.most_watched),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_WATCHED,
        name: translate(CommonEnum.most_watched),
      },
      {
        label: translate(CommonEnum.newest),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_NEW,
        name: translate(CommonEnum.newest),
      },
      {
        label: translate(CommonEnum.future_releases),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_RELEASE_CALENDAR,
        name: translate(CommonEnum.future_releases),
      },
      {
        label: translate(CommonEnum.lowest_ask),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_LOWEST_ASK_ASC,
        name: translate(CommonEnum.lowest_ask),
      },
      {
        label: translate(CommonEnum.highest_bid),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_HIGHEST_BID_DESC,
        name: translate(CommonEnum.highest_bid),
      },
      {
        label: translate(CommonEnum.alphabetical),
        value: envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_ALPHABET_ASC,
        name: translate(CommonEnum.alphabetical),
      },
    ];

    return allSortsCustomTranslated;
  });

  useEffect(() => {
    const hasAskSearchParam = searchParams?.get('hasAsk') === 'true';
    setIsHasAskToggleSelected(hasAskSearchParam);
  }, [searchParams]);

  return (
    <div>
      <SkusSearchLayout
        tCommon={tCommon}
        t={t}
        metaTitle={metaTitle}
        brandsContent={brandsContent}
        customBaseUrl={customBaseUrl}
        countrySlug={countrySlug}
      >
        {brandsContent && brandsContent.banner_image ? <ConfigureFix hitsPerPage={21} /> : null}
        <ResponsiveComponent
          windowWidth={windowWidth}
          DesktopComponent={
            <SkusDesktop
              allSortsCustomTranslated={allSortsCustomTranslated}
              tCommon={tCommon}
              wishlistContext={wishlistContext}
              user={user ?? null}
              push={router.push}
              windowWidth={windowWidth}
              h1Title={h1Title}
              brandsContent={brandsContent}
              isHasAskToggleSelected={isHasAskToggleSelected}
              includeCategoryNav={includeCategoryNav}
            />
          }
          MobileComponent={
            <SkusMobile
              allSortsCustomTranslated={allSortsCustomTranslated}
              hasFilterCookie={hasFilterCookie}
            />
          }
        />
      </SkusSearchLayout>
      {brandsContent && brandsContent.banner_image ? (
        <SkusBrandContent brandsContent={brandsContent}></SkusBrandContent>
      ) : null}
    </div>
  );
};

export default SkusEntirePage;
